import { Box, LinearProgress, Snackbar } from '@mui/material';
import { useRef, useState } from 'react';
import { notify } from '../../../Utils/toastify';
import { uploadToS3 } from '../../../Utils/AwsService';
import { pdfSchema, startOrderSchema } from '../../../Utils/yupvalidations';
import Images from '../../../Utils/images';

// import { commonUploadFn } from '../../../Utils/AwsService'

interface Props {
  setProgress?: any;
  loading?: boolean;
  setLoad?: (f: boolean) => void;
  title?: string;
  ratio?: any;
  type?: string;
  onAddDocument?: any;
  onlyDocs?: boolean;
  onlyImage?: boolean;
  uploadProgress?: any;
  disabled?: boolean;
  documents?: any[]; // add this
}

/**
 * Handles file upload and image cropping functionality for the component.
 * @example
 * ImageCropperInput({
 *   setProgress: function(progress) { /* update progress */
export const ImageCropperInput = ({
  setProgress,
  loading,
  setLoad,
  title,
  ratio,
  type,
  onAddDocument,
  onlyDocs,
  onlyImage,
  uploadProgress,
  documents,
}: Props) => {
  const imageRef = useRef<HTMLInputElement>(null);

  // const [imgUrl, setImgUrl] = useState<any | null>(null);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  // const [originalFile, setOriginalFile] = useState<any>(null);

  const handleClick = (e: any) => {
    if (loading) return;
    if (imageRef && imageRef.current) {
      imageRef.current.value = ''; // clear previous file
      imageRef.current.click();
    }
  };

  // const handleCroppedImage = (image: any) => {
  //   // 🛑 Check document upload limit
  //   const maxDocs = 5;
  //   if ((documents?.length ?? 0) >= 5) {
  //     notify(`You can only attach up to ${maxDocs} documents.`, 'error');
  //     return;
  //   }

  //   handleUpload(image);
  // };

  /**
   * Uploads a file to S3 and handles status updates and callbacks for adding the document.
   * @example
   * sync(file)
   * undefined
   * @param {File} file - File object representing the file to be uploaded.
   * @returns {void} No return value.
   * @description
   *   - Uses a callback function `onAddDocument` to send document details after upload.
   *   - Updates loading states through `setLoad` and `setLoadingStatus`.
   *   - Fetches the uploaded document's URL via `uploadToS3`.
   */
  const handleUpload = async (file: File) => {
    setLoad?.(true);
    setLoadingStatus(true);
    const fileURL: any = await uploadToS3(file, setProgress);
    console.log('Total', file.size);
    if (onAddDocument) {
      // Call the callback with document details
      onAddDocument({
        deptId: selectedDeptId, // Set this dynamically based on your application logic
        docName: file.name,
        type: file.type,
        url: fileURL?.url,
        size: file.size,
      });
    }
    setLoad?.(false);
    setLoadingStatus(false);
  };

  /**
   * Handles the selection and processing of image and document files from an input element.
   * @example
   * sync(event)
   * undefined
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event triggered by file input change.
   * @returns {Promise<void>} Executes various operations based on file type; may notify user on errors.
   * @description
   *   - Supports various image formats, including 'jpeg', 'png', 'jpg', 'heic', and 'heif'.
   *   - Validates file size for image and document files to ensure they are below 100MB.
   *   - Limits the number of attachments to a maximum of 20 documents.
   *   - Converts HEIC/HEIF images to JPEG format and provides notification on failure.
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const isDuplicate = documents?.some(
        doc => doc.docName?.toLowerCase?.() === file.name.toLowerCase(),
      );

      if (isDuplicate) {
        notify('Document already exist', 'error');
        return;
      }
      const fileType = file.type.toLowerCase();
      const fileName = file.name.toLowerCase();
      const totalSizeBytes =
        (documents?.reduce((total, doc) => total + doc.size, 0) ?? 0) +
        file.size;
      const totalSizeMB = totalSizeBytes / (1024 * 1024);
      console.log('Total size in MB', totalSizeMB);
      if (totalSizeMB > 10) {
        notify('Max 10Mb allowed', 'error');
        return;
      }

      const fileSizeMB = file.size / 1024 / 1024;

      // Allowed file extensions
      const allowedImageExt = ['jpg', 'jpeg', 'png'];
      const allowedPdfExt = ['pdf'];
      const allowedExcelExt = ['xls', 'xlsx'];

      const fileExtension = fileName.split('.').pop();

      // 🔹 Size check
      if (fileSizeMB > 10) {
        notify('Max 10Mb allowed', 'error');
        return;
      }

      // 🔹 Max docs check
      const maxDocs = 5;
      if ((documents?.length ?? 0) >= maxDocs) {
        notify(`You can only attach up to ${maxDocs} documents.`, 'error');
        return;
      }

      try {
        if (allowedPdfExt.includes(fileExtension!)) {
          // PDF
          await pdfSchema.validateAt('file', { file });
          handleUpload(file);
        } else if (allowedExcelExt.includes(fileExtension!)) {
          // Excel
          handleUpload(file);
        } else if (allowedImageExt.includes(fileExtension!)) {
          // Images
          // setOriginalFile(file);
          await startOrderSchema.validateAt('Attachments', { file });
          handleUpload(file);
          // const reader = new FileReader();
          // reader.onload = () => setImgUrl(reader.result);
          // reader.readAsDataURL(file);
        } else {
          // ❌ Not allowed
          notify(
            'Invalid file type. Allowed: JPG, JPEG, PNG, PDF, XLS, XLSX',
            'error',
          );
          return;
        }
      } catch (error: any) {
        notify(error.message || 'File validation failed', 'error');
        setLoad?.(false);
        setLoadingStatus(false);
      }
    }
  };

  return (
    <>
      {/* {imgUrl && (
        <ImageCropper
          image={imgUrl}
          setImgUrl={setImgUrl}
          handleCroppedImage={(image: any) => handleCroppedImage(image)}
          ratio={ratio}
        />
      )} */}

      <div className={'imageItem'}>
        <div className="selectimg_video">
          <input
            type="file"
            aria-hidden="true"
            ref={imageRef}
            accept={'.jpg, .jpeg, .png, .pdf,.xls, .xlsx'}
            onClick={e => {
              if (documents && documents?.length >= 5) {
                e.preventDefault();
                notify(`You can only attach up to 5 documents.`, 'error');
              }
            }}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            id="image_pdf_id"
          />

          <label onClick={type && !onlyDocs ? () => {} : handleClick}>
            <img src={Images.IMAGE_IC} alt="Image" />
            <h4>
              {loading ? 'Uploading...' : title ? title : 'Add Thumbnail'}
            </h4>
            <p>
              <span>Upload max 5 files</span>
            </p>
          </label>
        </div>

        {uploadProgress && uploadProgress !== null && uploadProgress != 100 && (
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={true}
            message="I love snacks"
            sx={{
              width: '300px',
              zIndex: 99,
            }}
            className="ImageProgressBg"
          >
            <Box width={'100%'} mt={1} p={2} className="imageProgress">
              <LinearProgress variant="determinate" value={uploadProgress} />
              <p>{uploadProgress}%</p>
            </Box>
          </Snackbar>
        )}
      </div>
    </>
  );
};
